/* ============================================================
   CartManager – Global cart drawer controller
   Depends on: drawer.js (Drawer class), robin-functions.js (Robin global)
   ============================================================ */
window.CartManager = (function () {
  // ---- DOM references (populated in onDomLoaded) ----

  let _overlay, _loading, _empty, _items, _badge, _button;

  // ---- Drawer instance ----

  const _drawer = new Drawer({
    drawerID: 'cart-drawer-panel',
    buttonID: 'cart-drawer-button',
    extraInteractiveIDs: ['cart-drawer-overlay'],
    drawerAnimations: {
      enter: { x: ['0%', '-100%'] },
      exit: { x: ['-100%', '0%'] },
    },
    onToggle: function (ev) {
      if (!_overlay) return;
      if (ev.detail.isOpen) {
        _overlay.classList.remove('invisible', 'opacity-0');
        document.body.style.overflow = 'hidden';
      } else {
        _overlay.classList.add('invisible', 'opacity-0');
        document.body.style.overflow = 'auto';
      }
    },
    onDomLoaded: function () {
      _overlay = document.getElementById('cart-drawer-overlay');
      _loading = document.getElementById('cart-drawer-loading');
      _empty = document.getElementById('cart-drawer-empty');
      _items = document.getElementById('cart-drawer-items');
      _badge = document.getElementById('cart-badge-count');
      _button = document.getElementById('cart-drawer-button');

      if (_overlay) {
        _overlay.addEventListener('click', function () {
          CartManager.close();
        });
      }

      if (_items) {
        _items.addEventListener('click', handleItemClick);
      }

      window.addEventListener('cartUpdate', function () {
        refresh();
      });

      refresh();
    },
  });

  // ---- State management ----

  function showState(state) {
    if (_loading) _loading.classList.toggle('hidden', state !== 'loading');
    if (_empty) _empty.classList.toggle('hidden', state !== 'empty');
    if (_items) _items.classList.toggle('hidden', state !== 'items');
  }

  // ---- Fetch & Render ----

  async function refresh() {
    const cart = await Robin.getCart();
    if (!cart || !cart.items) return;

    const totalsFinalEl = document.getElementById('cart-drawer-totals-final');
    const totalsOriginalEl = document.getElementById('cart-drawer-totals-original');
    if (totalsFinalEl) {
      totalsFinalEl.textContent = Robin.getPrice(cart.total_price / 100);
    }
    if (totalsOriginalEl) {
      if (cart.original_total_price !== cart.total_price) {
        totalsOriginalEl.textContent = Robin.getPrice(cart.original_total_price / 100);
        totalsOriginalEl.classList.remove('hidden');
      } else {
        totalsOriginalEl.classList.add('hidden');
      }
    }

    const count = cart.item_count || 0;
    if (_badge) {
      _badge.textContent = count > 99 ? '99+' : String(count);
      _badge.classList.toggle('!hidden', count === 0);
    }
    if (_button) {
      _button.setAttribute('aria-label', 'Open cart, ' + count + ' items');
    }

    document.body.classList.toggle('empty-cart', cart.items.length === 0);

    if (cart.items.length === 0) {
      showState('empty');
      return;
    }

    renderItems(cart.items);
    showState('items');
  }

  function renderItems(items) {
    if (!_items) return;

    const template = document.getElementById('cart-item-template');
    _items.innerHTML = '';

    items.forEach(function (item) {
      const clone = template.content.cloneNode(true);
      const root = clone.querySelector('.cart-drawer-item');

      root.dataset.itemId = item.key;

      const img = clone.querySelector('img');
      img.src = item.featured_image.url;
      img.alt = item.featured_image.alt || item.product_title || '';

      clone.querySelector('[data-field="title"]').textContent = item.product_title;
      clone.querySelector('[data-field="variant-title"]').textContent = item.variant_title;
      clone.querySelector('[data-field="quantity"]').textContent = item.quantity;
      clone.querySelector('[data-field="price"]').textContent = Robin.getPrice(item.final_line_price / 100);

      const originalPriceEl = clone.querySelector('[data-field="original-price"]');
      if (originalPriceEl && item.original_line_price !== item.final_line_price) {
        originalPriceEl.textContent = Robin.getPrice(item.original_line_price / 100);
        originalPriceEl.classList.remove('hidden');
      }

      if (item.selling_plan_allocation) {
        clone.querySelector('[data-field="suscription"]').classList.remove('hidden');
        const contentCollapse = clone.querySelector('[data-field="suscription-content-collapse"]');
        const button = clone.querySelector('[data-field="suscription-button"]');
        button.setAttribute(
          'aria-controls',
          'subscription collapse content - ' + item.title + '-' + item.selling_plan_allocation.selling_plan.name,
        );

        clone.querySelector('[data-field="suscription-time"]').textContent =
          item.selling_plan_allocation.selling_plan.name;
        clone.querySelector('[data-field="suscription-payment"]').textContent = Robin.getPrice(
          item.selling_plan_allocation.price / 100,
        );
        const contentHeight = clone.querySelector('[data-field="suscription-content"]');
        clone.querySelector('[data-field="suscription-button"]').addEventListener('click', function () {
          console.log(contentHeight.offsetHeight, contentCollapse, clone, '<--- Content Height');
          if (contentCollapse.offsetHeight === 0) {
            if (!contentCollapse.classList.contains('h-0')) {
              contentCollapse.classList.remove('h-0');
            }
            contentCollapse.style.height = contentHeight.offsetHeight + 'px';
          } else {
            contentCollapse.style.height = '0';
          }
        });
      }
      clone.querySelectorAll('[data-item-id]').forEach(function (el) {
        el.dataset.itemId = item.key;
      });
      clone.querySelectorAll('[data-qty]').forEach(function (el) {
        el.dataset.qty = item.quantity;
      });

      const itemLabel = item.product_title + (item.variant_title ? ' (' + item.variant_title + ')' : '');
      const removeBtn = clone.querySelector('.cart-action-remove');
      const downBtn = clone.querySelector('.cart-action-down');
      const upBtn = clone.querySelector('.cart-action-up');
      if (removeBtn) removeBtn.setAttribute('aria-label', 'Remove ' + itemLabel);
      if (downBtn) downBtn.setAttribute('aria-label', 'Decrease quantity of ' + itemLabel);
      if (upBtn) upBtn.setAttribute('aria-label', 'Increase quantity of ' + itemLabel);

      _items.appendChild(clone);
    });
  }

  // ---- Item click handler (event delegation) ----

  async function handleItemClick(e) {
    const removeBtn = e.target.closest('.cart-action-remove');
    const downBtn = e.target.closest('.cart-action-down');
    const upBtn = e.target.closest('.cart-action-up');

    const btn = removeBtn || downBtn || upBtn;
    if (!btn) return;

    const itemId = btn.dataset.itemId;
    const itemRow = _items.querySelector('.cart-drawer-item[data-item-id="' + itemId + '"]');

    if (itemRow) {
      itemRow.classList.add('opacity-50', 'pointer-events-none');
    }

    if (removeBtn) {
      await Robin.updateQty(itemId, 0);
    } else if (downBtn) {
      await Robin.updateQty(itemId, parseInt(btn.dataset.qty, 10) - 1);
    } else if (upBtn) {
      await Robin.updateQty(itemId, parseInt(btn.dataset.qty, 10) + 1);
    }
  }

  // ---- Public API ----

  return {
    open: function () {
      if (!_drawer.isDrawerOpen) _drawer.toggle();
    },
    close: function () {
      if (_drawer.isDrawerOpen) _drawer.toggle();
    },
    toggle: function () {
      _drawer.toggle();
    },
    refresh: refresh,
  };
})();
